import * as React from 'react';
import SvgIcon, {type SvgIconProps} from '@mui/material/SvgIcon';

/** Shared Outlined stroke props (MUI 24�24 optical weight). */
const SW = 1.5;

function O(props: SvgIconProps & {children: React.ReactNode; label: string}) {
    const {children, label, ...rest} = props;
    return (
        <SvgIcon viewBox="0 0 24 24" inheritViewBox={false} aria-label={label} {...rest}>
            <g fill="none" stroke="currentColor" strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round">
                {children}
            </g>
        </SvgIcon>
    );
}

/** Admin root � shield with gear mark */
export function AdminRootOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Admin" {...props}>
            <path d="M12 3l7 3v5.5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
            <circle cx="12" cy="11" r="2.2" />
            <path d="M12 8.8v-.6M12 14.4v-.6M9.6 11h-.6M15 11h-.6" />
        </O>
    );
}

/** Company Info � building */
export function CompanyInfoOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Company Info" {...props}>
            <path d="M4 20h16" />
            <path d="M6 20V8l6-4 6 4v12" />
            <path d="M10 20v-5h4v5" />
            <path d="M9 11h1.5M13.5 11H15M9 14h1.5M13.5 14H15" />
        </O>
    );
}

/** Account / GL root � ledger book */
export function AccountRootOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Account" {...props}>
            <path d="M6 4.5h9.5A2.5 2.5 0 0 1 18 7v12.5H7.5A1.5 1.5 0 0 1 6 18V4.5z" />
            <path d="M6 4.5A1.5 1.5 0 0 0 4.5 6v12A1.5 1.5 0 0 0 6 19.5" />
            <path d="M9 9h6M9 12h6M9 15h4" />
        </O>
    );
}

/** Audit root � checklist clipboard */
export function AuditRootOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Audit" {...props}>
            <rect x="6" y="4" width="12" height="16" rx="1.5" />
            <path d="M9 4.5h6v2.5H9z" />
            <path d="M9 11l1.5 1.5L14 9" />
            <path d="M9 15.5h6" />
        </O>
    );
}

/** Finance Statements � document + scale */
export function FinanceStatementsOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Finance Statements" {...props}>
            <rect x="5" y="2.5" width="14" height="19" rx="1.25" />
            <path d="M8 6.5h8M8 9h6M8 11.5h4" />
            <path d="M7.5 15.5h9" />
            <circle cx="12" cy="15.5" r="1.1" />
            <path d="M8.5 15.5v2.5M15.5 15.5v2.5M7 18h3M14 18h3" />
        </O>
    );
}

/** Schedule / Adjustment � notepad with pencil */
export function ScheduleOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Schedule" {...props}>
            <path d="M7 4h8.5L18 6.5V19a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5.5A1.5 1.5 0 0 1 7 4z" />
            <path d="M8.5 9h5M8.5 12h5M8.5 15h3.5" />
            <path d="M14 16.5l3.5-3.5 1.5 1.5-3.5 3.5H14v-1.5z" />
        </O>
    );
}

/** Tax � receipt with % */
export function TaxOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Tax" {...props}>
            <path d="M8 3.5h8l1.5 2v14.5H6.5V5.5L8 3.5z" />
            <path d="M9 9.5h6M9 12.5h6M9 15.5h4" />
            <path d="M14.5 6.5l1.5 1.5" />
            <circle cx="10.5" cy="6.8" r="0.9" />
            <circle cx="15.2" cy="9.2" r="0.9" />
        </O>
    );
}

/** Loan � quote / document with currency cue */
export function LoanOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Loan" {...props}>
            <path d="M5.5 7A6.5 6.5 0 0 1 12 4.5 6.5 6.5 0 0 1 18.5 7" />
            <path d="M5.5 7v9.5L12 19.5l6.5-3V7" />
            <path d="M12 10v5.5" />
            <path d="M10 11.5c0-.8.9-1.5 2-1.5s2 .7 2 1.5-.9 1.3-2 1.3-2 .5-2 1.3.9 1.5 2 1.5 2-.7 2-1.5" />
        </O>
    );
}

/** AFS � wallet / available-for-sale */
export function AfsOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="AFS" {...props}>
            <rect x="3.5" y="7" width="17" height="11.5" rx="1.5" />
            <path d="M3.5 10h17" />
            <path d="M16 13.5h2.5a1.2 1.2 0 0 1 0 2.4H16" />
            <path d="M7 5.5h7.5L17 7" />
        </O>
    );
}

/** General Ledger � invoice sheet */
export function GeneralLedgerOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="General Ledger" {...props}>
            <path d="M7 3.5h8l3 3V19a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5z" />
            <path d="M15 3.5V7h3" />
            <path d="M8.5 10.5h7M8.5 13.5h7M8.5 16.5h4.5" />
        </O>
    );
}

/** Sys-Manage � sliders */
export function SysManageOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Sys-Manage" {...props}>
            <path d="M4 7h10M18 7h2" />
            <circle cx="16" cy="7" r="2" />
            <path d="M4 12h2M10 12h10" />
            <circle cx="8" cy="12" r="2" />
            <path d="M4 17h8M16 17h4" />
            <circle cx="14" cy="17" r="2" />
        </O>
    );
}

/** Category manage � tagged folder */
export function CateManageOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Cate-Manage" {...props}>
            <path d="M4 7.5V18a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 18V9.5A1.5 1.5 0 0 0 18.5 8H11L9 5.5H5.5A1.5 1.5 0 0 0 4 7v.5z" />
            <path d="M9 13h6M9 16h4" />
        </O>
    );
}

/** Dictionary � stacked books */
export function DicManageOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Dic-Manage" {...props}>
            <path d="M5 5.5h5.5v13H5z" />
            <path d="M10.5 5.5H16v13h-5.5z" />
            <path d="M16 7.5l3 1v11l-3-1.5" />
            <path d="M6.5 9h2.5M12 9h2.5" />
        </O>
    );
}

/** Bank dictionary */
export function BankDicOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Bank Dictionary" {...props}>
            <path d="M4 9l8-5 8 5" />
            <path d="M5.5 9.5V17h13V9.5" />
            <path d="M4 17.5h16" />
            <path d="M8 12.5v3M12 12.5v3M16 12.5v3" />
        </O>
    );
}

/** Equity transaction � swap arrows */
export function EquityTxOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Equity Transaction" {...props}>
            <path d="M4 9h12" />
            <path d="M13 6l3 3-3 3" />
            <path d="M20 15H8" />
            <path d="M11 12l-3 3 3 3" />
        </O>
    );
}

/** Tax deduction limit � rule / gauge */
export function TaxLimitOutlinedIcon(props: SvgIconProps) {
    return (
        <O label="Tax Deduction Limit" {...props}>
            <rect x="4.5" y="4.5" width="15" height="15" rx="2" />
            <path d="M8 12h8" />
            <path d="M12 8v8" />
            <path d="M8.5 8.5l7 7" />
        </O>
    );
}
